import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Button, Space, Tag, message, Modal, Input, Select, Tabs, Row, Col, Tooltip, Badge } from 'antd';
import { TeamOutlined, PlusOutlined, DeleteOutlined, EditOutlined, SearchOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import { groupApi, clientApi, tradingApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

export default function GroupPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('positions');

  // 编辑弹窗
  const [editModal, setEditModal] = useState({ open: false, name: '', clientIds: [], isNew: true, id: '', parentId: '' });

  // 成员弹窗
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [syncingGroup, setSyncingGroup] = useState(false);

  // 分组一键同步弹窗（与客户端管理同步交互一致：确认在线成员 -> 轮询逐条结果）
  const [syncVisible, setSyncVisible] = useState(false);
  const [syncMembers, setSyncMembers] = useState([]);   // 待同步在线成员 [{id,name}]
  const [syncRunning, setSyncRunning] = useState(false);
  const [syncTaskId, setSyncTaskId] = useState('');
  const [syncTask, setSyncTask] = useState(null);
  const [syncDone, setSyncDone] = useState(false);

  // 搜索过滤
  const [accountSearch, setAccountSearch] = useState('');
  const [posSearch, setPosSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [quoteMap, setQuoteMap] = useState({});

  const loadGroups = async () => {
    try {
      const res = await groupApi.list();
      if (res.success) setGroups(res.data || []);
    } catch { /* ignore */ }
  };

  const loadClients = async () => {
    try {
      const res = await clientApi.getOnline();
      if (res.success) setAllClients(res.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadGroups();
    loadClients();
  }, []);

  const loadGroupData = async (groupId) => {
    if (!groupId) return;
    setLoading(true);
    try {
      const [acctRes, posRes, ordRes] = await Promise.all([
        groupApi.getAccounts(groupId),
        groupApi.getPositions(groupId),
        groupApi.getOrders(groupId),
      ]);
      if (acctRes.success) setAccounts(acctRes.data || []);
      if (posRes.success) {
        const data = posRes.data || [];
        setPositions(data);
        // 获取实时行情（补零到6位）
        const codes = [...new Set(data.map(p => p.stock_code).filter(Boolean).map(c => String(c).padStart(6, '0')))];
        if (codes.length > 0) {
          tradingApi.getQuotes(codes).then(qres => {
            if (qres.success) {
              const m = {};
              (qres.data || []).forEach(q => { if (q.stock_code) m[q.stock_code] = q; });
              setQuoteMap(m);
            }
          }).catch(() => {});
        }
      }
      if (ordRes.success) setOrders(ordRes.data || []);
    } catch (e) { console.error('[GroupPage] loadGroupData error:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (selectedGroup) {
      loadGroupData(selectedGroup.id);
      loadClients();
    }
  }, [selectedGroup]);

  const openCreate = (parentId = '') => {
    setEditModal({ open: true, name: '', clientIds: [], isNew: true, id: '', parentId });
  };

  const openEdit = (g) => {
    setEditModal({ open: true, name: g.name, clientIds: g.selfClientIds || g.client_id_list || [], isNew: false, id: g.id, parentId: g.parent_id || '' });
  };

  // 分组一键同步：提交 -> 拿到 task_id -> 轮询逐条结果（与客户端管理同步一致）
  const startGroupSync = async () => {
    if (!selectedGroup) return;
    setSyncRunning(true);
    setSyncDone(false);
    setSyncTask(null);
    try {
      const res = await groupApi.syncAll(selectedGroup.id);
      if (!res.success) {
        message.error(res.message || '同步失败');
        setSyncRunning(false);
        return;
      }
      const taskId = res.data?.task_id;
      if (!taskId) {
        setSyncDone(true);
        setSyncRunning(false);
        return;
      }
      setSyncTaskId(taskId);
      let finished = false;
      let lastStatus = '';
      for (let i = 0; i < 300 && !finished; i++) {
        await new Promise(r => setTimeout(r, 2000));
        try {
          const t = await clientApi.getSyncTask(taskId);
          if (t.success && t.data) {
            setSyncTask(t.data);
            lastStatus = t.data.status;
            if (t.data.status === 'done') finished = true;
          }
        } catch { /* 忽略单次轮询错误，继续 */ }
      }
      setSyncDone(true);
      setSyncRunning(false);
      if (lastStatus !== 'done') message.warning('同步结果获取超时，请刷新页面查看');
    } catch {
      message.error('同步请求失败');
      setSyncRunning(false);
    }
  };

  const saveGroup = async () => {
    if (!editModal.name.trim()) {
      message.warning('请输入分组名称');
      return;
    }
    try {
      let res;
      if (editModal.isNew) {
        res = await groupApi.create({ name: editModal.name.trim(), clientIds: editModal.clientIds, parent_id: editModal.parentId });
      } else {
        res = await groupApi.update(editModal.id, { name: editModal.name.trim(), clientIds: editModal.clientIds, parent_id: editModal.parentId });
      }
      if (res.success) {
        message.success(editModal.isNew ? '创建成功' : '更新成功');
        setEditModal({ open: false, name: '', clientIds: [], isNew: true, id: '', parentId: '' });
        loadGroups();
      } else message.error(res.message || '操作失败');
    } catch (e) {
      console.error('保存分组失败', e);
      const detail = e?.response?.data?.message || e?.message || '操作失败';
      message.error(`操作失败: ${detail}`);
    }
  };

  const deleteGroup = (g) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定删除分组「${g.name}」？`,
      onOk: async () => {
        try {
          const res = await groupApi.delete(g.id);
          if (res.success) {
            message.success('已删除');
            if (selectedGroup?.id === g.id) {
              setSelectedGroup(null);
              setPositions([]);
              setOrders([]);
            }
            loadGroups();
          } else message.error(res.message);
        } catch { message.error('删除失败'); }
      },
    });
  };

  // 获取客户端名称
  const getClientName = (id) => {
    const c = allClients.find(x => x.id === id);
    return c ? c.name || id : id;
  };

  const getClientStatus = (id) => {
    const c = allClients.find(x => x.id === id);
    return c ? c.status : 'offline';
  };

  // 过滤账户
  const filteredAccounts = useMemo(() => {
    if (!accountSearch) return accounts;
    const s = accountSearch.toLowerCase();
    return accounts.filter(a =>
      (a.client_id || '').toLowerCase().includes(s) ||
      (a.client_name || getClientName(a.client_id) || '').toLowerCase().includes(s)
    );
  }, [accounts, accountSearch]);

  // 过滤持仓
  const filteredPositions = useMemo(() => {
    if (!posSearch) return positions;
    const s = posSearch.toLowerCase();
    return positions.filter(p =>
      (p.client_id || '').toLowerCase().includes(s) ||
      (getClientName(p.client_id) || '').toLowerCase().includes(s) ||
      (p.stock_code || '').toLowerCase().includes(s) ||
      (p.stock_name || '').toLowerCase().includes(s)
    );
  }, [positions, posSearch, allClients]);

  // 过滤委托
  const filteredOrders = useMemo(() => {
    if (!orderSearch) return orders;
    const s = orderSearch.toLowerCase();
    return orders.filter(o =>
      (o.client_id || '').toLowerCase().includes(s) ||
      (getClientName(o.client_id) || '').toLowerCase().includes(s) ||
      (o.stock_code || '').toLowerCase().includes(s) ||
      (o.stock_name || '').toLowerCase().includes(s)
    );
  }, [orders, orderSearch, allClients]);

  // 可勾选添加的客户端选项（排除已在组内的）
  const availableClients = allClients.filter(c => !editModal.clientIds.includes(c.id));

  // 构建分组树：顶层分组（parent_id 为空）为一级，子分组作为 children 缩进显示
  // 每个节点的 client_id_list 累加自身 + 所有子孙（去重），使"子分组相加 = 总分组"
  const treeGroups = useMemo(() => {
    const map = {};
    const roots = [];
    groups.forEach(g => { map[g.id] = { ...g, children: undefined, selfClientIds: g.client_id_list || [] }; });
    groups.forEach(g => {
      if (g.parent_id && map[g.parent_id]) {
        const p = map[g.parent_id];
        (p.children || (p.children = [])).push(map[g.id]);
      } else {
        roots.push(map[g.id]);
      }
    });
    // 递归累加子孙客户到父节点
    const accumulate = (node) => {
      let all = [...(node.selfClientIds || [])];
      let sub = 0;
      (node.children || []).forEach(ch => {
        sub += 1 + accumulate(ch);
        all = all.concat(ch._allClientIds || []);
      });
      // 去重
      const uniq = [];
      const seen = new Set();
      all.forEach(id => { if (!seen.has(id)) { seen.add(id); uniq.push(id); } });
      node._allClientIds = uniq;
      node._subCount = sub;
      node.client_id_list = uniq; // 展示/选中时使用合并后的全集
      return sub;
    };
    roots.forEach(accumulate);
    return roots;
  }, [groups]);

  // 主分组默认展开（子分组作为叶子节点，无展开图标）
  useEffect(() => {
    setExpandedKeys(treeGroups.filter(g => !g.parent_id).map(g => g.id));
  }, [treeGroups]);

  const groupSideColumns = [
    { title: '分组名称', dataIndex: 'name', key: 'name', render: (v, r) => (
      <span>
        {r.parent_id ? <Tag color="purple" style={{ marginRight: 4 }}>子</Tag> : null}
        <a onClick={() => { setSelectedGroup(r); setActiveTab('positions'); }}>{v}</a>
        {!r.parent_id && r._subCount > 0 ? (
          <span style={{ color: '#999', fontSize: 12, marginLeft: 6 }}>（含 {r._subCount} 个子分组）</span>
        ) : null}
      </span>
    )},
    { title: '在线/离线', width: 90, render: (_, r) => {
      const ids = r.client_id_list || [];
      const online = ids.filter(id => allClients.find(c => c.id === id)?.status === 'online').length;
      return <span><Tag color="green" style={{ marginRight: 2 }}>{online}在线</Tag><Tag>{ids.length - online}离线</Tag></span>;
    }},
    { title: '操作', width: 110, render: (_, r) => (
      <Space size={4}>
        {!r.parent_id ? (
          <Tooltip title="新建子分组"><Button size="small" icon={<PlusOutlined />} onClick={(e) => { e.stopPropagation(); openCreate(r.id); }} /></Tooltip>
        ) : null}
        <Tooltip title="编辑"><Button size="small" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); openEdit(r); }} /></Tooltip>
        <Tooltip title="删除"><Button size="small" danger icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); deleteGroup(r); }} /></Tooltip>
      </Space>
    )},
  ];

  const posColumns = [
    { title: '客户端', dataIndex: 'client_id', width: 120, render: v => {
      const name = getClientName(v);
      return <Tag style={{ cursor: 'pointer', color: '#1677ff' }} onClick={() => navigate(`/accounts?client_id=${encodeURIComponent(v || '')}`)}>{name === v ? v : `${name}(${v})`}</Tag>;
    }},
    { title: '股票', key: 'stock', width: 120, render: (_, r) => <span><b>{r.stock_name}</b><br /><small>{r.stock_code}</small></span> },
    { title: '数量', dataIndex: 'quantity', width: 70, render: v => v?.toLocaleString() },
    { title: '可用', dataIndex: 'available_qty', width: 70, render: v => v?.toLocaleString() },
    { title: '成本价', dataIndex: 'cost_price', width: 80, render: v => v?.toFixed(3) },
    { title: '市价', key: 'live_price', width: 70, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      if (q) {
        const color = q.change_percent > 0 ? '#e74c3c' : q.change_percent < 0 ? '#27ae60' : '#333';
        return <span style={{ fontWeight: 600, color }}>{q.current_price.toFixed(2)}</span>;
      }
      return r.current_price?.toFixed(2) || '-';
    }},
    { title: '市值', key: 'live_mv', width: 90, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      const price = q?.current_price || r.current_price || 0;
      return (price * (r.quantity || 0)).toLocaleString();
    }},
    { title: '盈亏', key: 'live_pl', width: 90, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      const price = q?.current_price || r.current_price || 0;
      const cost = r.cost_price || 0;
      const qty = r.quantity || 0;
      const pl = cost > 0 ? (price - cost) * qty : (r.profit_loss || 0);
      return (
        <span style={{ color: pl >= 0 ? '#e74c3c' : '#27ae60' }}>
          {pl >= 0 ? '+' : ''}{pl.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      );
    }},
    { title: '盈亏比', key: 'live_plr', width: 70, render: (_, r) => {
      const key = String(r.stock_code).padStart(6, '0');
      const q = quoteMap[key];
      const price = q?.current_price || r.current_price || 0;
      const cost = r.cost_price || 0;
      if (cost > 0 && q) {
        const ratio = (price - cost) / cost * 100;
        return <span style={{ color: ratio >= 0 ? '#e74c3c' : '#27ae60' }}>{ratio >= 0 ? '+' : ''}{ratio.toFixed(2)}%</span>;
      }
      return '-';
    }},
  ];

  const orderColumns = [
    { title: '客户端', dataIndex: 'client_id', width: 120, render: (v, r) => {
      const name = r.client_name || getClientName(v);
      return <Tag style={{ cursor: 'pointer', color: '#1677ff' }} onClick={() => navigate(`/accounts?client_id=${encodeURIComponent(v || '')}`)}>{name === v ? v : `${name}(${v})`}</Tag>;
    }},
    { title: '日期', dataIndex: 'order_date', width: 80, render: v => v || '-' },
    { title: '时间', dataIndex: 'order_time_str', width: 70, render: v => v || '-' },
    { title: '股票', key: 'stock', width: 110, render: (_, r) => `${r.stock_name}(${r.stock_code})` },
    { title: '方向', dataIndex: 'direction', width: 50, render: v => {
      const isBuy = v && (v.includes('买入') || v === 'buy');
      return <Tag color={isBuy ? 'red' : 'green'}>{isBuy ? '买' : '卖'}</Tag>;
    }},
    { title: '价格', dataIndex: 'price', width: 65, render: v => v?.toFixed(2) || '市价' },
    { title: '数量', dataIndex: 'quantity', width: 65, render: v => v?.toLocaleString() },
    { title: '成交', dataIndex: 'filled_qty', width: 65, render: v => v?.toLocaleString() || 0 },
    { title: '状态', dataIndex: 'status', width: 65, render: v => {
      const colorMap = { '已成': 'success', '已撤': 'default', '已报': 'processing', '部成': 'warning', '废单': 'error' };
      const color = Object.entries(colorMap).find(([k]) => v?.includes(k))?.[1] || 'default';
      return <Tag color={color}>{v || '-'}</Tag>;
    }},
  ];

  // 统计总市值和总盈亏（用实时行情，与明细表格一致）
  const livePriceOf = (p) => {
    const key = String(p.stock_code || '').padStart(6, '0');
    const q = quoteMap[key];
    return q?.current_price || p.current_price || 0;
  };
  const totalMarketValue = positions.reduce((s, p) => s + livePriceOf(p) * (p.quantity || 0), 0);
  const totalPL = positions.reduce((s, p) => {
    const price = livePriceOf(p);
    if (!p.cost_price || p.cost_price <= 0) return s;
    return s + (price - p.cost_price) * (p.quantity || 0);
  }, 0);

  return (
    <div>
      <Row gutter={16}>
        {/* 左侧：分组列表 */}
        <Col span={6}>
          <Card title={<Space><TeamOutlined /> 客户端分组</Space>} size="small"
            extra={<Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => openCreate()}>新建</Button>}>
            <Table dataSource={treeGroups} columns={groupSideColumns} rowKey="id" size="small"
              childrenColumnName="children"
              expandable={{
                expandedRowKeys: expandedKeys,
                onExpand: (expanded, record) => setExpandedKeys(prev => expanded ? [...new Set([...prev, record.id])] : prev.filter(k => k !== record.id)),
              }}
              pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
              locale={{ emptyText: '暂无分组' }}
              onRow={(r) => ({
                onClick: () => { setSelectedGroup(r); setActiveTab('accounts'); },
                style: { cursor: 'pointer', background: selectedGroup?.id === r.id ? '#bae7ff' : undefined },
              })}
              rowSelection={{ type: 'radio', selectedRowKeys: selectedGroup ? [selectedGroup.id] : [], onChange: (keys, rows) => { if (rows.length > 0) { setSelectedGroup(rows[0]); setActiveTab('accounts'); } } }}
            />
          </Card>
        </Col>

        {/* 右侧：分组详情 */}
        <Col span={18}>
          {selectedGroup ? (
            <Card
              title={<Space>
                <TeamOutlined />
                {selectedGroup.name}
                {(() => {
                  const ids = selectedGroup.client_id_list || [];
                  const online = ids.filter(id => allClients.find(c => c.id === id)?.status === 'online').length;
                  return <span><Tag color="green">{online}在线</Tag><Tag>{ids.length - online}离线</Tag></span>;
                })()}
              </Space>}
              size="small"
              extra={
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(selectedGroup)}>编辑成员</Button>
                  <Button size="small" icon={<ReloadOutlined />} onClick={() => { loadGroupData(selectedGroup.id); loadClients(); }}>刷新</Button>
                </Space>
              }
            >
              {/* 成员按钮 */}
              <div style={{ marginBottom: 12 }}>
                <Space wrap>
                  <Button size="small" icon={<SearchOutlined />} onClick={() => setShowMemberModal(true)}>
                    查看成员 ({(selectedGroup.client_id_list || []).length})
                  </Button>
                  <Button size="small" icon={<SyncOutlined />} loading={syncingGroup}
                    onClick={async () => {
                      const ids = selectedGroup.client_id_list || [];
                      const online = ids.filter(id => allClients.find(c => c.id === id)?.status === 'online');
                      if (online.length === 0) { message.warning('当前分组没有在线客户端'); return; }
                      setSyncMembers(online.map(id => {
                        const c = allClients.find(x => x.id === id);
                        return { id, name: c?.name || c?.client_name || id };
                      }));
                      setSyncVisible(true);
                    }}
                  >一键同步资金持仓</Button>
                </Space>
              </div>

              {/* 统计卡片 */}
              {(() => {
                const totalAsset = accounts.reduce((s, a) => s + (a.total_asset || 0), 0);
                const totalAvail = accounts.reduce((s, a) => s + (a.available || 0), 0);
                return (
                  <Row gutter={12} style={{ marginBottom: 12 }}>
                    <Col span={4}><Card size="small"><b>总资产</b><br /><span style={{ fontSize: 18, fontWeight: 600, color: '#2980b9' }}>{totalAsset.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></Card></Col>
                    <Col span={4}><Card size="small"><b>总可用</b><br /><span style={{ fontSize: 18, fontWeight: 600, color: '#27ae60' }}>{totalAvail.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></Card></Col>
                    <Col span={4}><Card size="small"><b>股票市值</b><br /><span style={{ fontSize: 18, fontWeight: 600 }}>{totalMarketValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></Card></Col>
                    <Col span={4}><Card size="small"><b>持仓盈亏</b><br /><span style={{ fontSize: 18, fontWeight: 600, color: totalPL >= 0 ? '#e74c3c' : '#27ae60' }}>{totalPL >= 0 ? '+' : ''}{totalPL.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></Card></Col>
                    <Col span={4}><Card size="small"><b>持仓</b><br /><span style={{ fontSize: 18, fontWeight: 600 }}>{positions.length} 条</span></Card></Col>
                    <Col span={4}><Card size="small"><b>委托</b><br /><span style={{ fontSize: 18, fontWeight: 600 }}>{orders.length} 条</span></Card></Col>
                  </Row>
                );
              })()}

              {/* 账户资金 + 持仓 + 委托 Tabs */}
              <Tabs activeKey={activeTab} onChange={setActiveTab} size="small" items={[
                { key: 'accounts', label: `账户资金 (${filteredAccounts.length})`, children: (
                  <div>
                    <Input.Search placeholder="搜索客户端名称/ID" value={accountSearch} onChange={e => setAccountSearch(e.target.value)}
                      style={{ width: 240, marginBottom: 8 }} allowClear prefix={<SearchOutlined />} />
                    <Table dataSource={filteredAccounts} columns={[
                    { title: '客户端', dataIndex: 'client_id', width: 120, render: (v, r) => {
                      const name = r.client_name || getClientName(v);
                      return <Tag style={{ cursor: 'pointer', color: '#1677ff' }} onClick={() => navigate(`/accounts?client_id=${encodeURIComponent(v || '')}`)}>{name === v ? v : `${name}(${v})`}</Tag>;
                    }},
                    { title: '总资产', dataIndex: 'total_asset', width: 100, render: v => <b>{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</b> },
                    { title: '可用金额', dataIndex: 'available', width: 90, render: v => <span style={{ color: '#27ae60' }}>{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span> },
                    { title: '股票市值', dataIndex: 'market_value', width: 90, render: v => v?.toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                    { title: '持仓盈亏', dataIndex: 'profit_loss', width: 90, render: v => (
                      <span style={{ color: v >= 0 ? '#e74c3c' : '#27ae60' }}>{v >= 0 ? '+' : ''}{v?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    )},
                    { title: '冻结', dataIndex: 'frozen', width: 70, render: v => v?.toLocaleString() },
                    { title: '可取', dataIndex: 'withdrawable', width: 70, render: v => v?.toLocaleString() },
                  ]} rowKey="id" size="small" pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} scroll={{ y: 400 }}
                    locale={{ emptyText: '暂无账户数据' }} />
                  </div>
                )},
                { key: 'positions', label: `持仓 (${filteredPositions.length})`, children: (
                  <div>
                    <Input.Search placeholder="搜索客户端ID/股票" value={posSearch} onChange={e => setPosSearch(e.target.value)}
                      style={{ width: 240, marginBottom: 8 }} allowClear prefix={<SearchOutlined />} />
                    <Table dataSource={filteredPositions} columns={posColumns} rowKey="id" size="small"
                      pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} scroll={{ y: 400 }} locale={{ emptyText: '暂无持仓数据' }} />
                  </div>
                )},
                { key: 'orders', label: `当日委托 (${filteredOrders.length})`, children: (
                  <div>
                    <Input.Search placeholder="搜索客户端ID/股票" value={orderSearch} onChange={e => setOrderSearch(e.target.value)}
                      style={{ width: 240, marginBottom: 8 }} allowClear prefix={<SearchOutlined />} />
                    <Table dataSource={filteredOrders} columns={orderColumns} rowKey="id" size="small"
                      pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }} scroll={{ y: 400 }} locale={{ emptyText: '暂无委托数据' }} />
                  </div>
                )},
              ]} />
            </Card>
          ) : (
            <Card size="small">
              <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>
                <TeamOutlined style={{ fontSize: 48, marginBottom: 16 }} /><br />
                请从左侧选择分组查看详情
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* 成员弹窗 */}
      <Modal title={`${selectedGroup?.name || ''} - 成员列表`} open={showMemberModal}
        onCancel={() => setShowMemberModal(false)} footer={null} width={500}>
        <Input.Search placeholder="搜索客户端名称或ID" value={memberSearch}
          onChange={e => setMemberSearch(e.target.value)} style={{ marginBottom: 12 }} allowClear prefix={<SearchOutlined />} />
        <Table dataSource={(selectedGroup?.client_id_list || []).filter(cid => {
          if (!memberSearch) return true;
          const s = memberSearch.toLowerCase();
          const c = allClients.find(x => x.id === cid);
          return cid.toLowerCase().includes(s) || (c?.name || '').toLowerCase().includes(s);
        }).map(cid => ({ cid, client: allClients.find(x => x.id === cid) }))} columns={[
          { title: '客户端ID', width: 180, render: (_, r) => <a onClick={() => navigate(`/accounts?client_id=${encodeURIComponent(r.cid || '')}`)} style={{ color: '#1677ff' }}>{r.cid}</a> },
          { title: '名称', render: (_, r) => r.client?.name ? <a onClick={() => navigate(`/accounts?client_id=${encodeURIComponent(r.cid || '')}`)} style={{ color: '#1677ff' }}>{r.client.name}</a> : '-' },
          { title: '状态', width: 80, render: (_, r) => {
            const status = r.client?.status || 'offline';
            return <Badge status={status === 'online' ? 'success' : 'default'} text={status === 'online' ? '在线' : '离线'} />;
          }},
        ]} rowKey="cid" size="small" pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          locale={{ emptyText: '暂无成员' }} />
      </Modal>

      {/* 分组同步弹窗：确认在线成员 -> 逐条执行并显示结果（与客户端管理同步一致） */}
      <Modal
        title={`分组同步 - ${selectedGroup?.name || ''}`}
        open={syncVisible}
        onCancel={() => { if (!syncRunning) { setSyncVisible(false); setSyncDone(false); setSyncTask(null); } }}
        footer={syncDone ? <Button type="primary" onClick={() => { setSyncVisible(false); setSyncDone(false); setSyncTask(null); setTimeout(() => loadGroupData(selectedGroup?.id), 1000); }}>完成</Button>
          : (syncRunning ? null : <Button type="primary" onClick={startGroupSync}>开始同步</Button>)}
        width={520}
        maskClosable={false}
        closable={!syncRunning}
      >
        {!syncRunning && !syncDone && (
          <div>
            <p style={{ color: '#666', marginBottom: 8 }}>将同步以下 <b>{syncMembers.length}</b> 个在线客户端的资金与持仓：</p>
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {syncMembers.map(m => (
                <div key={m.id} style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ color: '#1677ff' }}>{m.name}</span>
                  <span style={{ color: '#999', marginLeft: 8, fontSize: 12 }}>{m.id}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {syncRunning && (
          <div>
            <p style={{ color: '#666' }}>正在同步，请稍候...</p>
            {syncTask && (
              <div style={{ marginBottom: 8, color: '#666' }}>
                进度：成功 {syncTask.success || 0} / 失败 {syncTask.fail || 0} / 共 {syncTask.total || 0}
              </div>
            )}
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              {(syncTask?.results || []).map(d => (
                <div key={d.client_id} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span><span style={{ color: '#1677ff' }}>{d.client_name || d.client_id}</span></span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Tag color={d.account_synced ? 'success' : 'error'} style={{ marginRight: 0 }}>资金{d.account_synced ? '✓' : '✗'}</Tag>
                    <Tag color={d.position_synced ? 'success' : 'error'} style={{ marginRight: 0 }}>持仓{d.position_synced ? '✓' : '✗'}</Tag>
                    {d.status === 'running' ? <Tag color="processing">同步中</Tag>
                      : d.success ? <Tag color="success">成功</Tag>
                        : d.status === 'pending' ? <Tag color="default">等待中</Tag>
                          : <Tag color="error">失败</Tag>}
                    {d.message && <span style={{ color: '#999', marginLeft: 6, fontSize: 12 }}>{d.message}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {syncDone && (
          <div>
            <div style={{ marginBottom: 8 }}>
              <Tag color="success">成功 {syncTask?.success || 0}</Tag>
              <Tag color="error">失败 {syncTask?.fail || 0}</Tag>
              <Tag>共 {syncTask?.total || 0}</Tag>
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {(syncTask?.results || []).map(d => (
                <div key={d.client_id} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span><span style={{ color: '#1677ff' }}>{d.client_name || d.client_id}</span></span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Tag color={d.account_synced ? 'success' : 'error'} style={{ marginRight: 0 }}>资金{d.account_synced ? '✓' : '✗'}</Tag>
                    <Tag color={d.position_synced ? 'success' : 'error'} style={{ marginRight: 0 }}>持仓{d.position_synced ? '✓' : '✗'}</Tag>
                    {d.success ? <Tag color="success">成功</Tag> : <Tag color="error">失败</Tag>}
                    {d.message && <span style={{ color: '#999', marginLeft: 6, fontSize: 12 }}>{d.message}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* 新建/编辑弹窗 */}
      <Modal title={editModal.isNew ? '新建分组' : '编辑分组'} open={editModal.open}
        onOk={saveGroup} onCancel={() => setEditModal({ open: false, name: '', clientIds: [], isNew: true, id: '' })}
        okText="保存" cancelText="取消" width={500}>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>分组名称</div>
          <Input value={editModal.name} onChange={e => setEditModal({ ...editModal, name: e.target.value })} placeholder="输入分组名称" maxLength={50} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>父分组（留空=顶层分组，选择则创建为子分组）</div>
          <Select style={{ width: '100%' }}
            value={editModal.parentId || undefined}
            onChange={v => setEditModal({ ...editModal, parentId: v || '' })}
            placeholder="不选择 = 顶层分组"
            allowClear
            options={groups.filter(g => !g.parent_id && g.id !== editModal.id).map(g => ({ value: g.id, label: g.name }))}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>
            选择客户端
            <span style={{ marginLeft: 8, fontSize: 12, color: '#999' }}>
              {(() => {
                // 子分组：只能从所属主分组的客户中选择，且同主分组下子分组之间不重复
                if (editModal.parentId) {
                  const pg = groups.find(g => g.id === editModal.parentId);
                  const pIds = (pg?.client_id_list || []).filter(c => c);
                  // 兄弟子分组已占用的客户
                  const siblingUsed = new Set();
                  groups.forEach(g => {
                    if (g.parent_id === editModal.parentId && g.id !== editModal.id) {
                      (g.client_id_list || []).forEach(cid => siblingUsed.add(cid));
                    }
                  });
                  const avail = pIds.filter(c => !siblingUsed.has(c)).length;
                  return `主分组 ${pIds.length} 台，已被其他子分组占用 ${siblingUsed.size} 台，可选 ${avail} 台`;
                }
                const inOther = {};
                groups.forEach(g => {
                  if (editModal.isNew || g.id !== editModal.id) {
                    (g.client_id_list || []).forEach(cid => { inOther[cid] = true; });
                  }
                });
                const total = allClients.length;
                const occupied = Object.keys(inOther).length;
                return `已占用 ${occupied} 台，可选 ${total - occupied} 台`;
              })()}
            </span>
          </div>
          <Select mode="multiple" style={{ width: '100%' }}
            value={editModal.clientIds}
            onChange={v => setEditModal({ ...editModal, clientIds: v })}
            placeholder="搜索并选择客户端..."
            filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
            options={(() => {
              // 子分组：候选 = 父组客户(排除同父其它子分组占用) ∪ 全局未关联分组的客户
              if (editModal.parentId) {
                const pg = groups.find(g => g.id === editModal.parentId);
                const pIds = new Set((pg?.client_id_list || []).filter(c => c));
                // 本组及父组之外的组占用的客户
                const otherUsed = new Set();
                groups.forEach(g => {
                  if ((editModal.isNew || g.id !== editModal.id) && g.id !== editModal.parentId) {
                    (g.client_id_list || []).forEach(cid => otherUsed.add(cid));
                  }
                });
                // 同父其它子分组占用的客户（父组内不能再分）
                const siblingUsed = new Set();
                groups.forEach(g => {
                  if (g.parent_id === editModal.parentId && g.id !== editModal.id) {
                    (g.client_id_list || []).forEach(cid => siblingUsed.add(cid));
                  }
                });
                const c2g = {};
                groups.forEach(g => {
                  if ((editModal.isNew || g.id !== editModal.id) && g.id !== editModal.parentId) {
                    (g.client_id_list || []).forEach(cid => { if (!c2g[cid]) c2g[cid] = g.name || g.id; });
                  }
                });
                const fromParent = c => pIds.has(c.id);
                const notUsedElsewhere = c => !otherUsed.has(c.id);
                const notSibling = c => !siblingUsed.has(c.id) || editModal.clientIds.includes(c.id);
                return allClients
                  .slice()
                  .filter(c => (notSibling(c)) && (fromParent(c) || notUsedElsewhere(c)))
                  .sort((a, b) => (b.status === 'online') - (a.status === 'online'))
                  .map(c => ({
                    value: c.id,
                    disabled: fromParent(c) && otherUsed.has(c.id) && !editModal.clientIds.includes(c.id) ? false : Boolean(c2g[c.id]) && !fromParent(c),
                    label: `${c.name || c.id} (${c.id}) ${c.status === 'online' ? '🟢在线' : '⚪离线'}${fromParent(c) ? '（父组已有）' : (c2g[c.id] ? `（已在:${c2g[c.id]}）` : '（未分组）')}`,
                  }));
              }
              // 客户 → 所属分组名（本组自己除外）
              const c2g = {};
              groups.forEach(g => {
                if (editModal.isNew || g.id !== editModal.id) {
                  (g.client_id_list || []).forEach(cid => { if (!c2g[cid]) c2g[cid] = g.name || g.id; });
                }
              });
              // 列出全部客户：没有关联分组的可勾选；已关联其它分组的禁用并注明所属组
              return allClients
                .slice()
                .sort((a, b) => (b.status === 'online') - (a.status === 'online'))
                .map(c => {
                  const inGroup = c2g[c.id];
                  const used = Boolean(inGroup) && !editModal.clientIds.includes(c.id);
                  return {
                    value: c.id,
                    disabled: used,
                    label: `${c.name || c.id} (${c.id}) ${c.status === 'online' ? '🟢在线' : '⚪离线'}${inGroup ? `（已在:${inGroup}）` : '（未分组）'}`,
                  };
                });
            })()}
          />
        </div>
        {editModal.clientIds.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Space size={4} wrap>
              {editModal.clientIds.map(cid => {
                const c = allClients.find(x => x.id === cid);
                return <Tag key={cid} closable onClose={() => setEditModal({ ...editModal, clientIds: editModal.clientIds.filter(x => x !== cid) })}>
                  {c ? c.name || cid : cid}
                </Tag>;
              })}
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
}
